import * as React from 'react';
import { Navigate, Outlet } from 'react-router';
import { useQuery } from '@apollo/client';

import NoAccess from '../pages/NoAccess';

import { SiteLoader } from '../ui/loaders/SiteLoader';
import { AUTHENTICATED_QUERY } from '../../graphql/queries/authenticated';
import { AuthenticatedQuery, AuthenticatedQueryVariables } from '../../graphql/types';
import { UserRole } from '../../types/common';
import { getRoleFromScopes } from '../../utils/getRoleFromScopes';
import { useDsRouteParams } from '../../hooks/useDsRouteParams';
import { estateIdVar } from '../../../client/graphql/client/links/error';
import { Wootric } from '../features/wootric/Wootric';

interface IAuhtenticationContext {
  authenticated: boolean;
  role: UserRole | null | undefined;
  name: string | null | undefined;
}

const defaultContext: IAuhtenticationContext = {
  authenticated: false,
  role: null,
  name: null
};

export const AuthenticationContext =
  React.createContext<IAuhtenticationContext>(defaultContext);

interface Protected extends React.PropsWithChildren {}

const Protected: React.FC<Protected> = ({ children }) => {
  const { estateId } = useDsRouteParams();

  estateIdVar(estateId);

  const { data, loading, error } = useQuery<
    AuthenticatedQuery,
    AuthenticatedQueryVariables
  >(AUTHENTICATED_QUERY, {
    fetchPolicy: 'network-only',
    ssr: false
  });

  if (error?.graphQLErrors[0]?.extensions?.redirect) {
    return <Navigate to={error?.graphQLErrors[0]?.extensions?.redirect as string} />;
  } else if (error?.message) {
    return <NoAccess />;
  }

  if (loading) {
    return <SiteLoader text="Sjekker tilgang.." />;
  }

  const { success, redirect, me } = data?.authenticated || {};
  const { name, email, createdAt, scopes = [] } = me || {};

  if (redirect) return <Navigate to="/no-access" />;

  const role = getRoleFromScopes((scopes as string[]) || []);
  if (!role) return <Navigate to="/no-access" />;

  return (
    <AuthenticationContext.Provider
      value={{
        authenticated: !!success,
        role,
        name
      }}
    >
      {children ? children : <Outlet />}
      <Wootric
        accountToken="NPS-EU-8a850abc"
        role={role}
        email={email}
        createdAt={createdAt}
      />
    </AuthenticationContext.Provider>
  );
};

export default Protected;
